import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ChevronLeft, ChevronRight, AlertTriangle, Loader2, CalendarDays, Settings,
  ShieldAlert, Award, TrendingUp, Truck, DollarSign, Users, Package,
  ListTodo, CheckCircle2, Clock, Shield as ShieldIcon,
} from 'lucide-react';
import { Button } from './ui/button';
import { CATEGORIES, CATEGORY_MAP, DEFAULT_LEGEND, getCategoryConfig } from '../lib/categories';
import { useBoards } from '../context/BoardContext';
import { listIssues } from '../lib/api';
import { daysInMonth, monthLabel, toISODate } from '../lib/date';
import EmptyBoard from './EmptyBoard';
import LetterMosaic from './LetterMosaic';
import BoardSettingsDialog from './BoardSettingsDialog';
import IssueListDialog from './IssueListDialog';
import IssueEditDialog from './IssueEditDialog';
import { usePermissions } from '../context/PermissionsContext';

const ICON_MAP = { ShieldAlert, Award, TrendingUp, Truck, DollarSign, Users, Package };

const BoardView = ({ onNavigate }) => {
  const { activeBoard } = useBoards();
  const perms = usePermissions();
  const [monthDate, setMonthDate] = useState(new Date());
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [dialogState, setDialogState] = useState({
    open: false,
    categoryKey: null,
    date: null,
  });
  const [openIssues, setOpenIssues] = useState([]);
  const [issueEditor, setIssueEditor] = useState({ open: false, existing: null });

  const year = monthDate.getFullYear();
  const monthIndex = monthDate.getMonth();
  const totalDays = daysInMonth(year, monthIndex);
  const today = new Date();
  const todayIso = toISODate(today);

  const days = useMemo(
    () => Array.from({ length: totalDays }, (_, i) => new Date(year, monthIndex, i + 1)),
    [year, monthIndex, totalDays]
  );

  const loadIssues = useCallback(async () => {
    if (!activeBoard) return;
    setLoading(true);
    try {
      const start = toISODate(new Date(year, monthIndex, 1));
      const end = toISODate(new Date(year, monthIndex, totalDays));
      const data = await listIssues(activeBoard.id, { start_date: start, end_date: end });
      setIssues(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [activeBoard, year, monthIndex, totalDays]);

  useEffect(() => {
    loadIssues();
  }, [loadIssues]);

  const loadOpenIssues = useCallback(async () => {
    if (!activeBoard?.id) return;
    try {
      const data = await listIssues(activeBoard.id, { open_only: true });
      setOpenIssues(data);
    } catch (e) {
      console.error(e);
    }
  }, [activeBoard?.id]);

  useEffect(() => {
    loadOpenIssues();
  }, [loadOpenIssues]);

  // Index: issuesIndex[category][date] = [issue, issue, ...]
  const issuesIndex = useMemo(() => {
    const idx = {};
    CATEGORIES.forEach((c) => (idx[c.key] = {}));
    for (const i of issues) {
      if (!idx[i.category]) idx[i.category] = {};
      if (!idx[i.category][i.date]) idx[i.category][i.date] = [];
      idx[i.category][i.date].push(i);
    }
    return idx;
  }, [issues]);

  const stats = useMemo(() => {
    let repeats = 0, closed = 0, open = 0;
    for (const i of issues) {
      if (i.repeat_issue) repeats++;
      if (i.is_closed) closed++;
      else if (i.color !== 'green' && i.color !== 'grey') open++;
    }
    return { total: issues.length, repeats, closed, open };
  }, [issues]);

  const goPrevMonth = () => setMonthDate(new Date(year, monthIndex - 1, 1));
  const goNextMonth = () => setMonthDate(new Date(year, monthIndex + 1, 1));
  const goToday = () => setMonthDate(new Date());

  const openDayCell = (categoryKey, dayIndex) => {
    const dayDate = days[dayIndex];
    setDialogState({ open: true, categoryKey, date: toISODate(dayDate) });
  };

  const openIssueFromPanel = (issue) => {
    setIssueEditor({ open: true, existing: issue });
  };

  const handleIssueEditorClose = (changed) => {
    setIssueEditor({ open: false, existing: null });
    if (changed) {
      loadOpenIssues();
      loadIssues();
    }
  };

  if (!activeBoard) return <EmptyBoard />;

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-slate-900">{activeBoard.name}</h1>
            <span className="px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold bg-slate-100 text-slate-600 rounded">
              {activeBoard.tier}
            </span>
          </div>
          <div className="text-sm text-slate-500 mt-1">
            Owner: {activeBoard.owner_email}
            {activeBoard.description && ` • ${activeBoard.description}`}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => setSettingsOpen(true)}>
            <Settings className="h-4 w-4 mr-1.5" />
            Board Settings
          </Button>
          <div className="h-6 w-px bg-slate-200" />
          <Button variant="outline" size="sm" onClick={goPrevMonth}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-2 px-4 h-9 rounded-md border border-slate-200 bg-white min-w-[200px] justify-center">
            <CalendarDays className="h-4 w-4 text-slate-500" />
            <span className="font-semibold text-slate-800">{monthLabel(monthDate)}</span>
          </div>
          <Button variant="outline" size="sm" onClick={goNextMonth}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={goToday}>Today</Button>
        </div>
      </div>

      {/* Stats (Open Issues clickable, no more Active) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <ClickableStat
          label="Open Issues"
          value={stats.open}
          color="#dc2626"
          bg="#fee2e2"
          icon={ListTodo}
          onClick={() => onNavigate && onNavigate('tasks', { statusFilter: 'open' })}
          hint="Click to view list"
        />
        <StatChip label="Repeat Issues" value={stats.repeats} color="#b45309" bg="#fef3c7" icon={AlertTriangle} />
        <StatChip label="Closed" value={stats.closed} color="#166534" bg="#dcfce7" icon={CheckCircle2} />
        <StatChip label="Total Entries" value={stats.total} color="#0369a1" bg="#e0f2fe" />
      </div>

      {/* Open Issues panel */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ListTodo className="h-4 w-4 text-slate-500" />
            <h2 className="text-sm font-semibold text-slate-800">Open Issues</h2>
          </div>
          <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-red-100 text-red-700">
            {openIssues.length}
          </span>
        </div>
        {openIssues.length === 0 ? (
          <div className="text-sm text-slate-500 text-center py-6">No open issues 🎉</div>
        ) : (
          <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
            {openIssues.map((issue) => {
              const cat = CATEGORY_MAP[issue.category];
              const catConfig = getCategoryConfig(activeBoard, issue.category);
              const status =
                catConfig.statuses.find((s) => s.key === issue.color) ||
                DEFAULT_LEGEND.find((l) => l.key === issue.color) ||
                DEFAULT_LEGEND[2];
              const detailText =
                issue.notes ||
                Object.values(issue.details || {}).filter(Boolean)[0] ||
                'No description';
              return (
                <button
                  key={issue.id}
                  onClick={() => openIssueFromPanel(issue)}
                  className="w-full flex items-center gap-3 rounded-md border border-slate-200 hover:border-[#0090D8] hover:bg-sky-50 p-2 text-left transition-colors"
                >
                  <div
                    className="w-7 h-7 rounded-md flex items-center justify-center text-white font-bold text-xs flex-shrink-0"
                    style={{ background: cat?.color || '#64748b' }}
                  >
                    {issue.category}
                  </div>
                  <div
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ background: status.color }}
                    title={status.label}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-slate-800 truncate">{detailText}</div>
                    <div className="text-[11px] text-slate-500">
                      {issue.date}
                      {issue.owner_email && ` · ${issue.owner_email}`}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* SQPDCHI Letter Mosaic Board */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 overflow-x-auto">
        <div className="grid grid-cols-7 gap-2 min-w-[1100px]">
          {CATEGORIES.map((cat) => {
            const Icon = ICON_MAP[cat.icon];
            const catIssues = issuesIndex[cat.key] || {};
            const catConfig = getCategoryConfig(activeBoard, cat.key);
            const legendMap = catConfig.statuses.reduce((acc, s) => {
              acc[s.key] = s;
              return acc;
            }, {});
            if (!legendMap.grey) {
              legendMap.grey = DEFAULT_LEGEND.find((l) => l.key === 'grey');
            }
            const openCount = Object.values(catIssues).flat().filter(
              (i) => i.color !== 'green' && i.color !== 'grey' && !i.is_closed
            ).length;
            const totalEntries = Object.values(catIssues).flat().length;
            const canEditThisCat = perms.canEdit(cat.key);
            return (
              <div key={cat.key} className={`flex flex-col items-center ${!canEditThisCat ? 'opacity-70' : ''}`}>
                <div className="mb-3">
                  <LetterMosaic
                    letterKey={cat.key}
                    days={days}
                    issuesByDate={catIssues}
                    legendMap={legendMap}
                    onDayClick={(dayIdx) => openDayCell(cat.key, dayIdx)}
                    todayIso={todayIso}
                    cellSize={22}
                    gap={3}
                  />
                </div>
                <div className="flex items-center gap-2 mt-2 pt-3 border-t border-slate-100 w-full justify-center">
                  <div
                    className="w-7 h-7 rounded-md flex items-center justify-center text-white font-bold text-sm shadow-sm relative"
                    style={{ background: cat.color }}
                  >
                    {cat.key}
                    {!canEditThisCat && (
                      <div className="absolute -bottom-1 -right-1 w-3.5 h-3.5 rounded-full bg-slate-700 border-2 border-white flex items-center justify-center" title="Read-only">
                        <svg className="h-2 w-2 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6z"/></svg>
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-slate-800 leading-tight">
                      {cat.label}
                    </div>
                    <div className="flex items-center gap-1 text-[10px] text-slate-500">
                      {Icon && <Icon className="h-3 w-3" />}
                      <span>{openCount} open · {totalEntries} total</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {loading && (
        <div className="flex items-center gap-2 justify-center py-3 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading...
        </div>
      )}

      {/* Legend & indicators */}
      <div className="mt-6 flex flex-wrap items-center gap-4 text-xs text-slate-600">
        <div className="font-semibold text-slate-800">Card indicators:</div>
        <IndicatorChip icon={AlertTriangle} color="#f59e0b" label="Repeat Issue" />
        <IndicatorChip icon={Clock} color="#ea580c" label="Temporary CM" />
        <IndicatorChip icon={ShieldIcon} color="#2563eb" label="Permanent CM" />
        <IndicatorChip icon={CheckCircle2} color="#16a34a" label="Closed" />
        <div className="flex items-center gap-1.5 text-slate-700">
          <span className="w-4 h-4 rounded-[3px] border border-slate-300"
                style={{ background: 'linear-gradient(135deg, #fee2e2 0%, #fee2e2 50%, #fef9c3 50%, #fef9c3 100%)' }} />
          <span>Multi-color = multiple issues on same day</span>
        </div>
        <button
          onClick={() => setSettingsOpen(true)}
          className="text-[#0090D8] hover:underline ml-auto"
        >
          Configure category legend, fields & team →
        </button>
      </div>

      <IssueListDialog
        open={dialogState.open}
        onOpenChange={(open) => setDialogState((s) => ({ ...s, open }))}
        categoryKey={dialogState.categoryKey}
        date={dialogState.date}
        onChanged={loadIssues}
      />

      <IssueEditDialog
        open={issueEditor.open}
        onOpenChange={(v) => {
          if (!v) handleIssueEditorClose(false);
        }}
        onSaved={() => handleIssueEditorClose(true)}
        onDeleted={() => handleIssueEditorClose(true)}
        categoryKey={issueEditor.existing?.category}
        date={issueEditor.existing?.date}
        existing={issueEditor.existing}
      />

      <BoardSettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        onSaved={() => setSettingsOpen(false)}
      />
    </div>
  );
};

const IndicatorChip = ({ icon: Icon, color, label }) => (
  <div className="flex items-center gap-1.5">
    <div
      className="w-5 h-5 rounded-full flex items-center justify-center border-2 border-white shadow-sm"
      style={{ background: color }}
    >
      <Icon className="h-2.5 w-2.5 text-white" strokeWidth={3} />
    </div>
    <span>{label}</span>
  </div>
);

const StatChip = ({ label, value, color, bg, icon: Icon }) => (
  <div
    className="rounded-lg border p-3 flex items-center gap-3"
    style={{ background: bg, borderColor: color + '40' }}
  >
    {Icon && (
      <div
        className="w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0"
        style={{ background: color + '20' }}
      >
        <Icon className="h-4 w-4" style={{ color }} />
      </div>
    )}
    <div className="min-w-0">
      <div className="text-xs font-medium truncate" style={{ color }}>{label}</div>
      <div className="text-2xl font-bold text-slate-800">{value}</div>
    </div>
  </div>
);

const ClickableStat = ({ label, value, color, bg, icon: Icon, onClick, hint }) => (
  <button
    onClick={onClick}
    className="rounded-lg border p-3 flex items-center gap-3 text-left hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer group"
    style={{ background: bg, borderColor: color + '60' }}
  >
    {Icon && (
      <div
        className="w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform"
        style={{ background: color + '25' }}
      >
        <Icon className="h-5 w-5" style={{ color }} />
      </div>
    )}
    <div className="min-w-0">
      <div className="text-xs font-semibold truncate" style={{ color }}>{label}</div>
      <div className="text-2xl font-bold text-slate-800">{value}</div>
      <div className="text-[10px] text-slate-500 mt-0.5">{hint}</div>
    </div>
  </button>
);

export default BoardView;
